/**
 * Reopen a terminal (succeeded / cancelled) task. Re-enters the state
 * machine at a chosen work state with a new run, and records the reason
 * in the event log so the audit trail captures *why* it was reopened.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { RotateCcw } from 'lucide-react';
import type { WorkState } from '@agentflow/shared';

interface Props {
  taskId: string;
  taskName: string;
  /** Where the task currently is — affects the "back to …" wording. */
  currentState: 'succeeded' | 'cancelled';
  onReopened?: () => void;
}

export function ReopenTaskDialog({
  taskId,
  taskName,
  currentState,
  onReopened,
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const [resumeFrom, setResumeFrom] = useState<WorkState>('execute');
  const [reason, setReason] = useState('');

  const reopen = useMutation({
    mutationFn: () =>
      api.reopenTask(taskId, {
        resume_from: resumeFrom,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      setOpen(false);
      onReopened?.();
    },
  });

  const trigger = currentState === 'succeeded' ? 'Reopen task' : 'Reopen task';
  const verbed = currentState === 'succeeded' ? 'completed' : 'cancelled';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="default">
          <RotateCcw className="h-4 w-4 mr-1.5" />
          {trigger}
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reopen this task?</DialogTitle>
          <DialogDescription>
            <span className="block">
              <span className="font-medium">{taskName}</span> is currently {verbed}. Reopening
              starts a new run from the work state you choose. The original run history is
              preserved.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Resume from</Label>
            <Select value={resumeFrom} onValueChange={(v) => setResumeFrom(v as WorkState)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="triage">Triage — re-classify and re-plan</SelectItem>
                <SelectItem value="plan">Plan — keep triage, re-plan</SelectItem>
                <SelectItem value="execute">Execute — keep plan, redo the work</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Most issues found post-completion go back to <em>execute</em>.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reopen-reason" className="text-xs text-muted-foreground">
              Reason (optional, recorded in the event log)
            </Label>
            <Textarea
              id="reopen-reason"
              rows={2}
              placeholder="e.g. discovered the implementation missed the edge case for empty input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        {reopen.isError && (
          <p className="text-sm text-destructive">Failed to reopen: {String(reopen.error)}</p>
        )}

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" disabled={reopen.isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={() => reopen.mutate()} disabled={reopen.isPending}>
            {reopen.isPending ? 'Reopening…' : 'Reopen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
